import { useEffect, useMemo, useState } from "react";
import './App.css';
import '@iq/models/styles.css'
import './index.css';
import { filterAppCatalog, NavBar, PageHeader } from '@iq/models'
import useUserStore from "./states/user";
import useAvailabilityStore from "./states/availability";
import { AiOutlineCalendar } from "react-icons/ai";
import { IoReceiptOutline } from "react-icons/io5";
import useIsSmallScreen from "./hooks/useIsSmallScreen.js"
import { Link, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { APP_CATALOG, DEFAULT_ENABLED_APP_IDS } from "./navigation/appCatalog";

import Calendar from "./pages/Calendar";
import Availability from "./pages/Availability";
import LoadingScreen from "./components/LoadingScreen/LoadingScreen";

import useAppointmentStore from "./states/appointment"; 

function getCurrentMonthKey() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

const ERP_URL =
  process.env.REACT_APP_ERP_URL || "http://localhost:3009";

const AUTH_SESSION_URL =
  process.env.REACT_APP_AUTH_SESSION_URL || "http://localhost:8086/auth/session";
const AUTH_LOGOUT_URL =
  process.env.REACT_APP_AUTH_LOGOUT_URL || "http://localhost:8086/auth/logout";
const UNAUTH_REDIRECT_URL =
  process.env.REACT_APP_UNAUTH_REDIRECT_URL || ERP_URL;
const LOGIN_URL = `${UNAUTH_REDIRECT_URL.replace(/\/$/, "")}/login-client`;
const APP_SUITE_DASHBOARD_URL =
  process.env.REACT_APP_APP_SUITE_DASHBOARD_URL || ERP_URL;
const HOME_LINK = { href: APP_SUITE_DASHBOARD_URL };
const CURRENT_APP = APP_CATALOG.find((app) => app.id === "calendar");
const PAGE_LINKS = [
  { id: "calendar", label: "Calendar", to: "/calendar", icon: <AiOutlineCalendar size={24} /> },
  { id: "availability", label: "Availability", to: "/availability", icon: <IoReceiptOutline size={24} /> },
];

function App({ enabledAppIds = DEFAULT_ENABLED_APP_IDS }) {
  const [loggingOut, setLoggingOut] = useState(false);
  const location = useLocation();
  const isSmallScreen = useIsSmallScreen();
  const session = useUserStore((state) => state.session);
  const setSession = useUserStore((state) => state.setSession);
  const userId = session?.profile?.id;
  const userName = [session?.profile?.first_name, session?.profile?.last_name].filter(Boolean).join(" ");
  const clearUser = useUserStore((state) => state.clearUser);
  const fetchCurrentWeekAvailability = useAvailabilityStore((state) => state.fetchCurrentWeekAvailability);
  const fetchAvailabilityExceptions = useAvailabilityStore((state) => state.fetchAvailabilityExceptions);
  const clearAvailability = useAvailabilityStore((state) => state.clearAvailability);
  const clearExceptions = useAvailabilityStore((state) => state.clearExceptions);
  const fetchAppointments = useAppointmentStore((state) => state.fetchAppointments);
  const clearAppointments = useAppointmentStore((state) => state.clearAppointments);
  const activeId = location.pathname.toLowerCase().startsWith("/availability")
    ? "availability"
    : "calendar";
  const activePage = PAGE_LINKS.find((item) => item.id === activeId).label;
  const displayName = userName || `User ${userId}`;
  const permittedApps = session?.permitted_apps;
  const appLinks = useMemo(() => filterAppCatalog(
    filterAppCatalog(APP_CATALOG, enabledAppIds),
    permittedApps ?? []
  ), [enabledAppIds, permittedApps]);

  async function handleLogout() {
    setLoggingOut(true);
    try {
      await fetch(AUTH_LOGOUT_URL, {
        method: "POST",
        credentials: "include",
      });
    } finally {
      clearAvailability();
      clearAppointments();
      clearUser();
      window.location.replace(LOGIN_URL);
    }
  }

  useEffect(() => {
    let isActive = true;

    async function validateSession() {
      try {
        const response = await fetch(AUTH_SESSION_URL, {
          method: "GET",
          credentials: "include",
        });

        if (response.status === 401 || response.status === 403) {
          clearExceptions();
          clearAvailability();
          clearAppointments();
          clearUser();
          window.location.replace(LOGIN_URL);
          return;
        }

        if (!response.ok) {
          throw new Error(`Session check failed (${response.status})`);
        }

        const payload = await response.json();
        const nextProfile = payload?.profile || null;

        setSession(payload);

        if (!isActive) {
          return;
        }

        if (nextProfile?.id) {
          await Promise.allSettled([
            fetchCurrentWeekAvailability(nextProfile.id),
            fetchAvailabilityExceptions(nextProfile.id),
            fetchAppointments(nextProfile.id, getCurrentMonthKey()),
          ]);
          return;
        }

        clearExceptions();
        clearAvailability();
        clearAppointments();
      } catch (error) {
        console.error("Session check failed:", error);
      }
    }

    validateSession();

    return () => {
      isActive = false;
    };
  }, [clearAppointments, clearAvailability, clearExceptions, clearUser, fetchAppointments, fetchAvailabilityExceptions, fetchCurrentWeekAvailability, setSession]);

  return (
    <div className={`crm-layout app${isSmallScreen ? " crm-layout-mobile" : ""}`}>
      <LoadingScreen open={loggingOut} message="Signing out..." />
      {!isSmallScreen && (
        <div className="nav-section">
              <NavBar
                activeId={activeId}
                navItems={PAGE_LINKS}
                LinkComponent={Link}
                homeLink={HOME_LINK}
              />
        </div>
      )}
      {!isSmallScreen && (
        <div className="page-logo-container">
          {CURRENT_APP?.image && <CURRENT_APP.image className="nav-logo"/>}
        </div>
      )}
      <PageHeader
        title={activePage}
        apps={appLinks}
        navItems={PAGE_LINKS}
        activeId={activeId}
        activeAppId="calendar"
        LinkComponent={Link}
        homeLink={HOME_LINK}
        isSmallScreen={isSmallScreen}
        user={{ name: displayName }}
        logoutAction={{ onClick: handleLogout }}
        userMenu={{ name: displayName, onLogout: handleLogout }}
      />
      <Routes>
        <Route path="/" element={<Navigate to="/calendar" replace />} />
        <Route path="/calendar" element={<Calendar />} />
        <Route path="/availability" element={<Availability />} />
        <Route path="*" element={<Navigate to="/calendar" replace />} />
      </Routes>
    </div>
  );
}

export default App;
